import React, { useContext, useMemo } from 'react';
import { VisuallyHidden } from '@balance-web/a11y';
import { Text } from '@balance-web/text';
import { forwardRefWithAs, makeIdFactory } from '@balance-web/utils';
import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes } from '@balance-web/core';

import { FieldContext } from './context';

const FieldLabelIdFactory = makeIdFactory('field-label');

type LabelProps = WithDataAttributeProp<{
  id?: string;
  htmlFor: string;
  visible?: boolean;
}>;

export const FieldLabel = forwardRefWithAs<'label', LabelProps>(
  ({ as = 'label', visible = true, ...props }, ref) => {
    const testids = FieldLabelIdFactory();
    const fieldContext = useContext(FieldContext);
    const testid = useMemo(() => {
      const existing = getTestIdFromDataAttributes(props.data);
      if (existing) {
        return existing;
      }

      if (props.id) {
        return testids.base(props.id);
      }

      return testids.base(fieldContext?.id);
    }, []);

    if (!visible) {
      return (
        <VisuallyHidden
          as={as}
          data-balance-component="field-label-hidden"
          data-testid={testid}
          {...props}
        />
      );
    }

    return (
      <Text
        ref={ref}
        as={as}
        cursor="default"
        data={{ testid }}
        data-balance-component="field-label"
        size="small"
        weight="medium"
        {...props}
      />
    );
  }
);
